import { ButtonType, ButtonWidget, TextLinkWidget } from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { IEditableServerSettings } from '../../settings/ServerApi/types/IEditableServerSettings';
import { ServerSettingsInstance } from '../../settings/services/ServerSettings';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import showResetStartPageDialog from '../services/ResetStartPage';
import { StartPageService } from '../services/startPage/StartPageService';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import React = require('react');

import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/siteManagement/services/resources/Sites';

const DEEPLINK_ID = 'start-page';

export type StartPageSettingProps = {
  startPage: string;
  isViewerSelf: boolean;
  isViewerAdmin: boolean;
  sessionSiteUrlName: string;
  serverUser: IServerUserInfo;
  isServerUserSubplace: boolean;
}


export function StartPageSetting(props: StartPageSettingProps): JSX.Element {
  const [sanitizedStartPage, setSanitizedStartPage] = React.useState<string>('');
  const [startPageHref, setStartPageHref] = React.useState<string>('');

  React.useEffect(
    () => {
      if (!props.startPage) {
        if (props.isViewerAdmin && props.isServerUserSubplace) {
          ServerSettingsInstance.fetch()
          .then((result: IEditableServerSettings) => {
            let startPage = result.defaultStartPage || '/';
            setSanitizedStartPage(startPage);
            setStartPageHref(StartPageService.getStartPageForSite(startPage, props.sessionSiteUrlName));
          });
        } else if (props.isViewerAdmin && !props.isServerUserSubplace) {
          Sites.instance.getCurrentSiteSettingsForSiteAdmin()
          .then((result: ISiteSettingsForSiteAdmin) => {
            let siteAdminSettings = result.siteAdminSettings as ISiteAdminSettings;
            let startPage = siteAdminSettings.siteStartPageUri;
            setSanitizedStartPage(startPage);
            setStartPageHref(StartPageService.getStartPageForSite(startPage, props.sessionSiteUrlName));
          });
        } else {
          let startPage = getSessionManagementServiceInstance().getStartPage();
          setSanitizedStartPage(startPage);
          setStartPageHref(StartPageService.getStartPageForSite(startPage, props.sessionSiteUrlName));
        }
      } else {
        setSanitizedStartPage(props.startPage);
        setStartPageHref(StartPageService.getStartPageForSite(props.startPage, props.sessionSiteUrlName));
      }
    },
    [props.startPage, props.sessionSiteUrlName, props.isViewerAdmin, props.isServerUserSubplace]
  );
  return (
    <DeepLink deepLinkId={DEEPLINK_ID}>
      <div style={{ display: 'flex' }}>
          <div data-tb-test-id="start-page-setting">
            <div className={UserSettingsStyle.settingsLabelText}>
              <h4>{Messages.settings_startPage_title()}</h4>
            </div>
          </div>
          <div className ={UserSettingsStyle.settingsLabelDescription}>
            <div className={UserSettingsStyle.detailsValue}>
              {
                props.isViewerSelf && getHint(props.isViewerAdmin)
              }
            </div>
            <div data-tb-test-id="start-page-description">
              <div className={UserSettingsStyle.detailsValue}>
                <span>{Messages.settings_startPage_current_start_page()}</span>
                <span>
                <TextLinkWidget
                href={'#' + startPageHref}
                testId={'start-page-link'}
                children={sanitizedStartPage === '' ? Messages.settings_startPage_link_home() : sanitizedStartPage}
                target='_blank'/>
                {!props.startPage && Messages.reserStartPage_default()}
                </span>
                <span>&nbsp;&nbsp;</span>
                <span>
                  <ButtonWidget
                  buttonType={ButtonType.Outline}
                  testId='reset-to-default-start-page'
                  disabled={!startPageHref || !props.startPage}
                  onClick={() => showResetStartPageDialog(props.serverUser)}
                  children={Messages.resetStartPage_button()}
                  />
                </span>
              </div>
            </div>
          </div>
      </div>
    </DeepLink>
  );
}

function getHint(isViewerAdmin: boolean) {
  const message = isViewerAdmin? Messages.startPageHint_admin(): Messages.startPageHint_user();
  return(
    <div data-tb-test-id = "start-page-hint">
      {message}
    </div>
  );
}

export const StartPageSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () => Messages.settings_startPage_title(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  tags:  () => { return [Messages.startPageShort(), Messages.userSettings_Preference_Header()]; },
  isAdmin: false
});